import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"

import { Button, FormControl, Grid, TextField, Typography } from "@material-ui/core"
import { Notification, Portlet } from "@/components"

import { TrackableLinkUTMMetricResponse, TrackableLinkUTMTypesResponse } from "@/protocols/trackableLinks"
import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkUTMMetrics/styles"
import TrackableLinkUTMMetricsSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkUTMMetrics/TrackableLinkUTMMetricsSkeleton"
import TrackableLinkUTMValue from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkUTMMetrics/TrackableLinkUTMValue"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import { TableColumn } from "@/components/DataTable"
import { fullDatetime } from "@/utils/time"
import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"

export type UTMMetricsTableSearchFilters = {
	searchByUTMType: string | null
	searchByUTMValue: string | null
}

export type FormattedUTMMetricsDataResponse = {
	type: string
	value: string
	clicksCount: number
	sentMessagesCount: number
	createdAt: Date
}

type FormattedUTMMetricsData = {
	utmMetrics: FormattedUTMMetricsDataResponse[]
	count: number
}

type FormattedUtmTypesData = {
	defaultUtmTypes: TrackableLinkUTMTypesResponse["defaultUtmTypes"]
	customUtmTypes: TrackableLinkUTMTypesResponse["customUtmTypes"]
}

const utmMetricsTableColumns: TableColumn<FormattedUTMMetricsDataResponse>[] = [
	{ field: "type", label: "Tipo de UTM" },
	{ field: "value", label: "Valor", rowRender: (value) => <TrackableLinkUTMValue utmValue={value as string} /> },
	{ field: "clicksCount", label: "Quantidade de cliques" },
	{ field: "sentMessagesCount", label: "Quantidade de mensagens enviadas" },
	{ field: "createdAt", label: "Criado em", rowRender: (value) => fullDatetime(new Date(value)) }
]

const TrackableLinkUTMMetrics: React.FC = () => {
	const { trackableLinkId } = useParams<{ trackableLinkId?: string }>()
	const { validation, triggerValidation, clearValidation } = useValidation()

	const [trackableLinkUTMMetrics, setTrackableLinkUTMMetrics] = useState<FormattedUTMMetricsData>({
		utmMetrics: [],
		count: 0
	})
	const [trackableLinkUtmTypes, setTrackableLinkUtmTypes] = useState<FormattedUtmTypesData>({
		defaultUtmTypes: [],
		customUtmTypes: []
	})
	const [loading, setLoading] = useState(false)
	const [paginationState, setPaginationState] = useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [utmListSearchFilters, setUtmListSearchFilters] = useState<UTMMetricsTableSearchFilters>({
		searchByUTMType: null,
		searchByUTMValue: null
	})

	const classes = useStyles()

	const handleGetTrackableLinkUTMMetrics = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkUTMMetricResponse>(`/trackable-links/${trackableLinkId}/utm-metrics`, {
				params: {
					searchByUTMType: utmListSearchFilters.searchByUTMType,
					searchByUTMValue: utmListSearchFilters.searchByUTMValue,
					rowsPerPage: paginationState.rowsPerPage,
					page: paginationState.page
				}
			})

			setTrackableLinkUTMMetrics({
				utmMetrics: data.trackableLinkUTMMetrics,
				count: data.count
			})
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar as UTMs" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleGetUtmTypes = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkUTMTypesResponse>("/trackable-links/utm/types", {
				params: {
					trackableLinkId
				}
			})

			setTrackableLinkUtmTypes({
				defaultUtmTypes: data.defaultUtmTypes,
				customUtmTypes: data.customUtmTypes
			})

			return data
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar os tipos de UTM" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleChangeEditFilters = (data: Partial<UTMMetricsTableSearchFilters>) => {
		setUtmListSearchFilters({
			...utmListSearchFilters,
			...data
		})
	}

	const handlePaginationState = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleApplyFilters = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		clearValidation("searchByUTMValue")

		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		await handleGetTrackableLinkUTMMetrics()
	}

	const selectInputOptions = trackableLinkUtmTypes.defaultUtmTypes.concat(trackableLinkUtmTypes.customUtmTypes).map(type => {
		return {
			id: type,
			name: type
		}
	})

	const selectInputOptionsGroupBy = (type: { name: string }) => {
		if (trackableLinkUtmTypes.defaultUtmTypes.includes(type.name)) {
			return "Padrões"
		}
		return "Customizadas"
	}

	useEffect(() => {
		handleGetTrackableLinkUTMMetrics()
		handleGetUtmTypes()
	}, [paginationState])

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Typography
							variant="h5"
							className={classes.titleText}
						>
							UTMs do link
						</Typography>
					</Grid>
					<Grid item>
						<Typography
							variant="body1"
						>
							Deseja um relatório completo? Acesse a página: <Link to="/admin/report/utms-report">Relatório de UTMs</Link>
						</Typography>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<Portlet>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<form onSubmit={handleApplyFilters}>
								<Grid container spacing={1}>
									<Grid item xs={4}>
										<FormControl fullWidth>
											<SelectInput
												multiple={false}
												onChange={trackableLinkUtmType => handleChangeEditFilters({ searchByUTMType: trackableLinkUtmType?.name ?? null })}
												placeholder="Tipos de UTM"
												noOptionsText="Nenhum tipo de UTM encontrado"
												groupBy={(type => selectInputOptionsGroupBy(type))}
												options={selectInputOptions}
											/>
										</FormControl>
									</Grid>
									<Grid item xs={4}>
										<FormControl fullWidth>
											<TextField
												value={utmListSearchFilters.searchByUTMValue}
												onChange={
													({ target }) => handleChangeEditFilters({ searchByUTMValue: target.value?.trimStart() })
												}
												placeholder="Valor da UTM"
												fullWidth
												variant="outlined"
												helperText={validation.searchByUTMValue}
												error={!!validation.searchByUTMValue}
											/>
										</FormControl>
									</Grid>
									<Grid item>
										<Button
											variant="contained"
											color="primary"
											type="submit"
											className={classes.searchButton}
										>
											Buscar
										</Button>
									</Grid>
								</Grid>
							</form>
						</Grid>

						{loading ? (
							<Grid item xs={12}>
								<TrackableLinkUTMMetricsSkeleton columnList={utmMetricsTableColumns} />
							</Grid>
						) : (
							<Grid item xs={12}>
								<DataTableWithPagination
									columns={utmMetricsTableColumns}
									data={trackableLinkUTMMetrics.utmMetrics}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={trackableLinkUTMMetrics.count}
									onPaginationChange={handlePaginationState}
								/>
							</Grid>
						)}
					</Grid>
				</Portlet>
			</Grid>
		</Grid>
	)
}

export default TrackableLinkUTMMetrics
